import { Input } from "@legal-500/legal500-design-system";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import {
  BackgroundField,
  BlockItemList,
  HREF_PATTERN,
  HREF_TITLE,
  useSyncBlockContent,
  useUploadTracker,
} from "./blockEditorParts";
import type { HeroContent } from "./types";
import styles from "../CMS.module.css";

type HeroEditorProps = {
  value: HeroContent;
  onChange: (value: HeroContent) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

export function HeroEditor({ value, onChange, onUploadingChange }: HeroEditorProps) {
  const { control, register, watch } = useForm<HeroContent>({ defaultValues: value });
  const { fields, append, remove, move } = useFieldArray({ control, name: "links" });
  const { report } = useUploadTracker(onUploadingChange);
  useSyncBlockContent(watch, value, onChange);

  return (
    <div className={styles.cardEditorFields}>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="hero-title">
          Title <span className={styles.required}>*</span>
        </label>
        <Input
          id="hero-title"
          required
          pattern=".*\S.*"
          title="Enter a title"
          {...register("title")}
        />
      </div>

      <div className={styles.cardEditorFieldRow}>
        <div className={styles.formField}>
          <Controller
            name="imageUrl"
            control={control}
            render={({ field }) => (
              <CmsImageUploadField
                noun="image"
                recommendation="Recommended: a wide landscape image, 1600 × 600 pixels or larger."
                previewAlt="Hero image"
                value={field.value}
                onChange={field.onChange}
                onUploadingChange={pending => report("hero", pending)}
              />
            )}
          />
        </div>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="hero-image-alt">
            Image description (alt text)
            {watch("imageUrl") && <span className={styles.required}> *</span>}
          </label>
          <Input
            id="hero-image-alt"
            required={Boolean(watch("imageUrl"))}
            {...register("imageAlt")}
          />
        </div>
      </div>

      <BlockItemList
        noun="Link"
        keys={fields.map(field => field.id)}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => append({ label: "", href: "" })}
        renderItem={(index, key) => (
          <div className={styles.cardEditorFieldRow}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`hero-link-label-${key}`}>
                Label
              </label>
              <Input
                id={`hero-link-label-${key}`}
                required={Boolean(watch(`links.${index}.href`))}
                {...register(`links.${index}.label`)}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`hero-link-href-${key}`}>
                Link (href)
              </label>
              <Input
                id={`hero-link-href-${key}`}
                required={Boolean(watch(`links.${index}.label`))}
                pattern={HREF_PATTERN}
                title={HREF_TITLE}
                {...register(`links.${index}.href`)}
              />
            </div>
          </div>
        )}
      />

      <BackgroundField register={register} name="background" stored={value.background} />
    </div>
  );
}
